<!--
  AchBankDialog -- collects and verifies the distributor's bank account for
  ACH Direct Debit with Stripe's PAYMENT ELEMENT, then records the mandate.

  Flow (opened by PaymentsPage after AchBillingCard created the SetupIntent):
    1. show({client_secret}) -> load Stripe.js with the platform publishable
       key, create the Payment Element for that SetupIntent, mount it.
    2. Submit -> stripe.confirmSetup (redirect: "if_required"). Stripe drives
       instant bank linking and shows ITS mandate language; we write none.
    3. succeeded     -> POST /ach/confirm (confirmAchMandate) -> success.
       requires_action / processing (micro-deposits) -> "verification
       pending"; the mandate is NOT recorded as success.
       error         -> shown inline, nothing recorded.

  If Stripe succeeded but recording failed, retry goes straight to recording:
  re-confirming an already-succeeded SetupIntent would fail. All sentences
  come from achCopy.js.

  Stripe-hosted fields live in an iframe; raw bank details never enter this
  page's DOM.
-->
<template>
  <v-dialog
    v-model="open"
    persistent
    scrollable
    max-width="min(960px, 95vw)"
  >
    <v-card data-testid="ach-bank-dialog">
      <v-card-title class="text-wrap">
        <div class="d-flex align-center">
          <h1 v-if="phase === 'success'" class="text-wrap">{{ copy.successTitle }}</h1>
          <h1 v-else-if="phase === 'pending'" class="text-wrap">{{ copy.pendingTitle }}</h1>
          <h1 v-else-if="phase === 'canceled'" class="text-wrap">{{ copy.dialogTitle }}</h1>
          <h1 v-else class="text-wrap">{{ copy.dialogTitle }}</h1>
          <v-spacer />
          <v-progress-circular
            v-show="busy || preparing"
            :indeterminate="true"
            size="25"
            width="2"
          />
        </div>
      </v-card-title>
      <v-divider />

      <v-card-text v-if="phase === 'success'" data-testid="ach-bank-success">
        <p>{{ copy.successBody }}</p>
      </v-card-text>

      <v-card-text v-else-if="phase === 'pending'" data-testid="ach-bank-pending">
        <p>{{ copy.pendingBody }}</p>
        <p v-if="hostedUrl">
          <a
            :href="hostedUrl"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="ach-bank-hosted-link"
            >{{ copy.hostedVerificationLink }}</a
          >
        </p>
      </v-card-text>

      <v-card-text v-else-if="phase === 'canceled'" data-testid="ach-bank-canceled">
        <p>{{ copy.canceledBody }}</p>
      </v-card-text>

      <v-card-text v-else class="mt-4">
        <v-row>
          <v-col cols="12" md="5" data-testid="ach-bank-intro-col">
            <p class="ach-dialog-intro">{{ copy.dialogIntro }}</p>
            <p
              v-if="copy.debitAmountsExplainer"
              class="ach-dialog-intro"
              data-testid="ach-bank-debit-explainer"
            >
              {{ copy.debitAmountsExplainer }}
            </p>
          </v-col>
          <v-col cols="12" md="7" data-testid="ach-bank-element-col">
            <p
              v-if="succeededSetupIntentId && !errorMessage"
              class="ach-dialog-intro"
              data-testid="ach-bank-already-verified"
            >
              {{ copy.alreadyVerified }}
            </p>
            <p v-if="preparing" class="ach-dialog-intro">
              {{ copy.loadingForm }}
            </p>
            <p
              v-if="errorMessage"
              class="ach-dialog-error"
              data-testid="ach-bank-error"
            >
              {{ errorMessage }}
            </p>
            <!-- Stripe mounts the Payment Element (an iframe) here. -->
            <div
              ref="elementContainer"
              class="ach-payment-element"
              data-testid="ach-bank-payment-element"
            ></div>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions v-if="phase === 'form'" class="py-3 px-3">
        <v-btn
          variant="flat"
          data-testid="ach-bank-cancel"
          :disabled="busy"
          @click="close"
        >
          {{ copy.cancelButton }}
        </v-btn>
        <v-spacer />
        <v-btn
          variant="flat"
          color="primary"
          class="text-white"
          data-testid="ach-bank-submit"
          :disabled="!canSubmit"
          @click="submit"
        >
          {{ copy.submitButton }}
        </v-btn>
      </v-card-actions>
      <v-card-actions v-else class="py-3 px-3">
        <v-spacer />
        <v-btn
          variant="flat"
          color="primary"
          class="text-white"
          data-testid="ach-bank-done"
          @click="close"
        >
          {{ copy.doneButton }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { computed, nextTick, onBeforeUnmount, ref, shallowRef } from "vue";
import { loadStripe } from "@stripe/stripe-js";
import { resolvePublishableKey } from "@/services/stripePublishableKey";
import { confirmAchMandate } from "@/services/billingService";
import { ACH_COPY } from "./achCopy";

// SetupIntent statuses that mean "the bank is still being verified" (the
// micro-deposit path), as opposed to a failure.
const PENDING_STATUSES = ["requires_action", "processing"];

// Stripe-hosted verification pages live on stripe.com (and subdomains). The
// URL comes from the server, so never render or open one that is not https
// on a stripe.com host. Returns the normalized href or "".
function safeStripeUrl(raw) {
  if (typeof raw !== "string" || !raw) return "";
  let u;
  try {
    u = new URL(raw);
  } catch (e) {
    return "";
  }
  const host = u.hostname.toLowerCase();
  const onStripe = host === "stripe.com" || host.endsWith(".stripe.com");
  if (u.protocol !== "https:" || !onStripe || u.username || u.password) {
    return "";
  }
  return u.href;
}

export default {
  name: "AchBankDialog",

  props: {
    // The platform publishable key from the plan payload. Falls back to the
    // build-time key (resolvePublishableKey) when the server has none.
    stripePublishableKey: {
      type: String,
      default: null,
    },
  },

  // completed: mandate recorded. pending: bank verification still running.
  emits: ["completed", "pending"],

  setup(props, { emit }) {
    const open = ref(false);
    // "form" | "success" | "pending"
    const phase = ref("form");
    const preparing = ref(false);
    const busy = ref(false);
    const errorMessage = ref("");
    const elementContainer = ref(null);
    // Validated Stripe-hosted micro-deposit verification link ("" = none).
    const hostedUrl = ref("");

    // shallowRef: Stripe objects are opaque class instances (no deep
    // proxying), and canSubmit must react to them being ready.
    const stripe = shallowRef(null);
    let stripeKey = null;
    const elements = shallowRef(null);
    const paymentElement = shallowRef(null);
    let clientSecret = null;
    // Bumped on every open/close so a slow loadStripe can't mount into a
    // dialog that has since been closed or reopened.
    let generation = 0;

    // Set once Stripe reports the SetupIntent succeeded, so a failed
    // recording retries the recording only. KEPT across close/reopen: the
    // bank is already linked at Stripe, so a reopen must retry /ach/confirm
    // for this same intent, never start over.
    const succeededSetupIntentId = ref(null);

    const canSubmit = computed(
      () =>
        !busy.value &&
        !preparing.value &&
        (!!succeededSetupIntentId.value ||
          (!!stripe.value && !!paymentElement.value))
    );

    function teardown() {
      if (paymentElement.value) paymentElement.value.destroy();
      paymentElement.value = null;
      elements.value = null;
      clientSecret = null;
    }

    onBeforeUnmount(() => {
      generation += 1;
      teardown();
    });

    async function prepare(gen) {
      preparing.value = true;
      try {
        const key = resolvePublishableKey({
          stripe_publishable_key: props.stripePublishableKey,
        });
        if (!stripe.value || stripeKey !== key) {
          stripe.value = await loadStripe(key);
          stripeKey = key;
        }
        if (gen !== generation) return;
        if (!stripe.value) {
          // loadStripe resolves null when the script is blocked/unreachable.
          errorMessage.value = ACH_COPY.stripeLoadError;
          return;
        }
        // Vuetify renders dialog content lazily (and teleported), so the
        // mount point can lag the open by a tick or two.
        for (let i = 0; i < 10 && !elementContainer.value; i += 1) {
          await nextTick();
        }
        if (gen !== generation) return;
        if (!elementContainer.value) {
          errorMessage.value = ACH_COPY.stripeLoadError;
          return;
        }
        elements.value = stripe.value.elements({ clientSecret });
        paymentElement.value = elements.value.create("payment", {
          layout: "tabs",
        });
        paymentElement.value.mount(elementContainer.value);
      } catch (e) {
        if (gen === generation) errorMessage.value = ACH_COPY.stripeLoadError;
      } finally {
        if (gen === generation) preparing.value = false;
      }
    }

    // Open for a freshly created SetupIntent ({client_secret, ...}). A second
    // call while open is ignored: one dialog, one Payment Element.
    async function show(setupData) {
      if (open.value) return;
      const secret = setupData && setupData.client_secret;
      if (!secret) return;
      hostedUrl.value = "";
      generation += 1;
      const gen = generation;
      clientSecret = secret;
      phase.value = "form";
      errorMessage.value = "";
      busy.value = false;
      open.value = true;
      // Backend reuse handoff: it found an existing billing SetupIntent.
      if (setupData.already_succeeded && setupData.setup_intent_id) {
        // Stripe already verified this bank; only recording is outstanding.
        succeededSetupIntentId.value = setupData.setup_intent_id;
        return;
      }
      if (setupData.pending_verification) {
        // Verification is running at Stripe: nothing to confirm, no form.
        hostedUrl.value = safeStripeUrl(setupData.hosted_verification_url);
        phase.value = "pending";
        emit("pending");
        return;
      }
      if (succeededSetupIntentId.value) {
        // Stripe already linked the account on an earlier open; only the
        // recording is outstanding. No new Payment Element needed.
        errorMessage.value = ACH_COPY.recordError;
        return;
      }
      // The mount point only exists once the dialog content has rendered.
      await nextTick();
      await prepare(gen);
    }

    function close() {
      if (busy.value) return;
      generation += 1;
      teardown();
      open.value = false;
      preparing.value = false;
      errorMessage.value = "";
    }

    async function record(setupIntentId) {
      try {
        await confirmAchMandate(setupIntentId);
      } catch (e) {
        const status = e && e.response && e.response.status;
        const data = (e && e.response && e.response.data) || {};
        if (
          status === 409 &&
          PENDING_STATUSES.includes(data.verification_status)
        ) {
          succeededSetupIntentId.value = null;
          phase.value = "pending";
          emit("pending");
          return;
        }
        errorMessage.value =
          status === 409 ? ACH_COPY.notVerifiedError : ACH_COPY.recordError;
        return;
      }
      succeededSetupIntentId.value = null;
      phase.value = "success";
      emit("completed");
    }

    async function submit() {
      if (!canSubmit.value) return;
      busy.value = true;
      errorMessage.value = "";
      try {
        if (succeededSetupIntentId.value) {
          await record(succeededSetupIntentId.value);
          return;
        }
        const result = await stripe.value.confirmSetup({
          elements: elements.value,
          confirmParams: {
            return_url: `${window.location.origin}/distributor/account/payments`,
          },
          redirect: "if_required",
        });
        const errIntent = result && result.error && result.error.setup_intent;
        if (errIntent && errIntent.status === "canceled") {
          phase.value = "canceled";
        } else if (result && result.error) {
          errorMessage.value = result.error.message || ACH_COPY.confirmError;
          return;
        }
        const intent = result && result.setupIntent;
        if (!intent) {
          errorMessage.value = ACH_COPY.confirmError;
        } else if (intent.status === "succeeded") {
          succeededSetupIntentId.value = intent.id;
          await record(intent.id);
        } else if (intent.status === "canceled") {
          // Terminal: a canceled SetupIntent cannot be retried with the same
          // client secret. Start over from the card.
          phase.value = "canceled";
        } else if (PENDING_STATUSES.includes(intent.status)) {
          phase.value = "pending";
          emit("pending");
        } else {
          errorMessage.value = ACH_COPY.confirmError;
        }
      } catch (e) {
        errorMessage.value = ACH_COPY.confirmError;
      } finally {
        busy.value = false;
      }
    }

    return {
      copy: ACH_COPY,
      open,
      phase,
      preparing,
      busy,
      errorMessage,
      elementContainer,
      hostedUrl,
      succeededSetupIntentId,
      canSubmit,
      show,
      close,
      submit,
    };
  },
};
</script>

<style scoped>
.ach-dialog-intro {
  margin-bottom: var(--tf-sp-3, 12px);
  color: var(--tf-fg-3, #56636e);
}
.ach-dialog-error {
  margin-bottom: var(--tf-sp-3, 12px);
  color: var(--tf-error, #b3261e);
}
.ach-payment-element {
  min-height: 40px;
}
</style>
